/**
 * Web replay tracking.
 *
 * Wraps the shared tracker queue with browser-specific auto-capture: one
 * delegated listener on the document records every click that lands on an
 * interactive element, so new buttons are covered without anyone remembering to
 * instrument them.
 *
 * Deliberately not captured: scrolls, mouse moves, and raw coordinates. They
 * multiply volume by an order of magnitude and none of them answer the question
 * a replay is for — who did what, and when.
 */

import {
  flushReplayEvents,
  initReplayTracker,
  resetReplayTracker,
  setReplayScreen,
  trackReplayEvent,
} from '@car-pool/shared/replayTracker';

const INTERACTIVE_SELECTOR = 'button, a, [role="button"], input, select, textarea, [data-track]';
const MAX_LABEL_LENGTH = 80;

let listenersAttached = false;

/**
 * The visible text of the control, which is what a person reviewing the replay
 * recognizes — "Call", "Send to carpool" — rather than a DOM id.
 */
function describeElement(element) {
  const explicit = element.getAttribute?.('data-track');
  if (explicit) return explicit;

  const label = element.getAttribute?.('aria-label')
    || element.title
    || element.value
    || element.textContent
    || '';

  const cleaned = String(label).replace(/\s+/g, ' ').trim();
  if (!cleaned) return element.tagName ? element.tagName.toLowerCase() : 'element';
  return cleaned.length > MAX_LABEL_LENGTH ? `${cleaned.slice(0, MAX_LABEL_LENGTH)}…` : cleaned;
}

// Elements can opt in to naming the student (or other subject) they act on, so
// a tap can be attributed to a row without the tracker knowing the board's shape.
function findSubject(element) {
  const holder = element.closest?.('[data-track-subject-id]');
  if (!holder) return {};

  const subjectId = Number.parseInt(holder.getAttribute('data-track-subject-id'), 10);
  if (!Number.isInteger(subjectId) || subjectId <= 0) return {};

  return {
    subjectType: holder.getAttribute('data-track-subject-type') || 'student',
    subjectId,
  };
}

function handleClick(event) {
  try {
    const target = event.target?.closest?.(INTERACTIVE_SELECTOR);
    if (!target) return;

    // Never record what someone typed — only that a field was used.
    const isSensitive = target.tagName === 'INPUT'
      && ['password', 'email', 'tel'].includes(target.type);

    trackReplayEvent(
      'ui.tap',
      {
        label: isSensitive ? `${target.type} field` : describeElement(target),
        element: target.tagName?.toLowerCase() || null,
        disabled: Boolean(target.disabled),
      },
      findSubject(target)
    );
  } catch {
    // A tracking failure must never interfere with the click itself.
  }
}

function handleVisibilityChange() {
  const hidden = document.visibilityState === 'hidden';
  trackReplayEvent('app.state', {
    to: hidden ? 'background' : 'active',
    from: hidden ? 'active' : 'background',
  });
  // Leaving the tab is a likely last moment to send anything.
  if (hidden) flushReplayEvents({ keepalive: true });
}

function handlePageHide() {
  trackReplayEvent('app.terminate', { reason: 'pagehide' });
  flushReplayEvents({ keepalive: true });
}

function handleOnline() {
  trackReplayEvent('net.error', { state: 'online' });
}

function handleOffline() {
  trackReplayEvent('net.error', { state: 'offline' });
}

/**
 * Start recording for a signed-in user. Idempotent — safe to call on every
 * render of the app shell.
 */
export function startWebReplayTracking({ baseUrl, token, app, schoolId, screen = null }) {
  initReplayTracker({
    baseUrl,
    token,
    app,
    platform: 'web',
    appVersion: process.env.REACT_APP_VERSION || null,
    schoolId,
  });

  if (screen) setReplayScreen(screen);

  if (listenersAttached || typeof document === 'undefined') return;
  listenersAttached = true;

  // Capture phase, so a click is recorded even when a handler stops propagation.
  document.addEventListener('click', handleClick, true);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', handlePageHide);
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  trackReplayEvent('app.launch', {
    referrer: document.referrer || null,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  });
}

export function stopWebReplayTracking() {
  if (listenersAttached && typeof document !== 'undefined') {
    document.removeEventListener('click', handleClick, true);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('pagehide', handlePageHide);
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    listenersAttached = false;
  }
  resetReplayTracker();
}

export { setReplayScreen, trackReplayEvent, flushReplayEvents };
